import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';
import { useCommonStore } from 'state/store/common/useCommonStore';

/** blocks-agents `/preferences/conversation`: how every agent addresses the user. Identity comes
 *  from the Blocks session, never from the request. */

export const PREFERENCE_LANGUAGES = [
  'en-US',
  'de-DE',
  'de-CH',
  'fr-FR',
  'fr-CH',
  'it-IT',
  'it-CH',
] as const;
export type PreferenceLanguage = (typeof PREFERENCE_LANGUAGES)[number];

export type AddressForm = 'formal' | 'informal';
export type Salutation = 'frau' | 'herr' | 'custom' | 'none';
export type PreferenceSource = 'onboarding' | 'settings';

export const INDUSTRY_CODES = [
  'finance_insurance',
  'industry_manufacturing',
  'trade_logistics_consumer',
  'health_pharma',
  'technology_media',
  'consulting_services',
  'construction_realestate_energy',
  'public_education',
  'other',
] as const;
export type IndustryCode = (typeof INDUSTRY_CODES)[number];

/** The backend refuses longer values with 400; the client checks them first. */
export const NAME_MAX_LENGTH = 80;
export const SALUTATION_TEXT_MAX_LENGTH = 40;
export const INDUSTRY_TEXT_MAX_LENGTH = 80;
export const WORK_AREA_MAX_LENGTH = 120;
export const TOOLS_MAX_LENGTH = 120;

export interface ConversationPreferences {
  uiLanguage: string | null;
  /** `unknown` until the user or an agent sets it. */
  addressForm: AddressForm | 'unknown';
  addressSource: string | null;
  givenName: string | null;
  familyName: string | null;
  salutation: Salutation | null;
  salutationText: string | null;
  /** The salutation was guessed from the name, not chosen: it shows [Anrede ändern]. */
  salutationInferred: boolean | null;
  industry: IndustryCode | null;
  industryText: string | null;
  workArea: string | null;
  /** The last onboarding question: the tools the user works with. */
  tools: string | null;
  /** 1 = the old web onboarding, 2 = the current one. Only rises. */
  onboardingVersion: number | null;
  /** Highest onboarding step answered or skipped (0-4). Goes down when the user goes back. */
  onboardingStep: number | null;
  onboardingCompletedAt: string | null;
  /** Times the one-time catch-up was put off with [Später]; it stops at 2. */
  catchUpSkips: number | null;
  revision: number;
}

/** A field left out stays unchanged; null clears it. `addressForm` can't be cleared. */
export type ConversationPreferencesPatch = Partial<{
  uiLanguage: PreferenceLanguage | null;
  addressForm: AddressForm;
  givenName: string | null;
  familyName: string | null;
  salutation: Salutation | null;
  salutationText: string | null;
  salutationInferred: boolean;
  industry: IndustryCode | null;
  industryText: string | null;
  workArea: string | null;
  tools: string | null;
  onboardingVersion: 1 | 2;
  onboardingStep: number;
  catchUpSkips: number;
}>;

/** What the backend refuses in free text, which goes into every agent's prompt: `<>` and the
 *  backtick, control and format characters (zero-width, bidi overrides), line and paragraph
 *  separators, and the Unicode tag block. */
const FORBIDDEN_TEXT = /[<>`\p{Cc}\p{Cf}\p{Zl}\p{Zp}\u{E0000}-\u{E007F}]/u;

/** True when the backend would store `value`: nonempty once trimmed, within `maxLength`, and free
 *  of the characters it refuses. Checked before anything is sent. */
export const isAcceptedPreferenceText = (value: unknown, maxLength: number): value is string =>
  typeof value === 'string' &&
  Boolean(value.trim()) &&
  value.trim().length <= maxLength &&
  !FORBIDDEN_TEXT.test(value);

export const isPreferenceLanguage = (code?: string | null): code is PreferenceLanguage =>
  PREFERENCE_LANGUAGES.includes(code as PreferenceLanguage);

/** A session without a durable identity (e.g. a widget) gets 403 and keeps no preferences. */
export const isPreferencesForbidden = (error: unknown) =>
  Boolean(error && typeof error === 'object' && 'status' in error && error.status === 403);

/** A network error, a timeout, rate limiting or a server error: the same write may succeed later.
 *  Any other 4xx rejects the content itself, so sending it again would fail again. */
export const isTransientPreferencesError = (error: unknown) => {
  const status =
    error && typeof error === 'object' && 'status' in error ? Number(error.status) : undefined;
  return !status || status >= 500 || status === 408 || status === 429;
};

const unavailableUsers = new Set<string>();
const currentUserId = () => useCommonStore.getState().user?.id;

/** True once this user's session was refused; writes are then skipped silently. */
export const arePreferencesUnavailable = (userId = currentUserId()) =>
  Boolean(userId && unavailableUsers.has(userId));

const rememberRefusal = (error: unknown) => {
  const userId = currentUserId();
  if (userId && isPreferencesForbidden(error)) unavailableUsers.add(userId);
};

const savedListeners = new Set<() => void>();

/** Called after every successful write, so mounted queries refetch. Returns the unsubscribe. */
export const onConversationPreferencesSaved = (listener: () => void) => {
  savedListeners.add(listener);
  return () => {
    savedListeners.delete(listener);
  };
};

const preferencesEndpoint = async () => {
  const config = await getConfigurationSettings();
  return {
    url: `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.conversationPreferences}`,
    blocksKey: config.blocksKey,
  };
};

export const getConversationPreferences = async (): Promise<ConversationPreferences> => {
  const { url, blocksKey } = await preferencesEndpoint();
  try {
    return await clients.get<ConversationPreferences>(url, blocksKey);
  } catch (error) {
    rememberRefusal(error);
    throw error;
  }
};

export const putConversationPreferences = async (
  patch: ConversationPreferencesPatch,
  source: PreferenceSource,
  signal?: AbortSignal
): Promise<ConversationPreferences> => {
  const { url, blocksKey } = await preferencesEndpoint();
  try {
    const body = JSON.stringify({ ...patch, source });
    const saved = await (signal
      ? clients.put<ConversationPreferences>(url, blocksKey, body, {}, signal)
      : clients.put<ConversationPreferences>(url, blocksKey, body));
    savedListeners.forEach((listener) => listener());
    return saved;
  } catch (error) {
    rememberRefusal(error);
    throw error;
  }
};

/** Best-effort write that never throws: skipped once the session was refused, retried once on
 *  any other failure. Resolves true when the change was saved. */
export const saveConversationPreferences = async (
  patch: ConversationPreferencesPatch,
  source: PreferenceSource
): Promise<boolean> => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (!currentUserId() || arePreferencesUnavailable()) return false;
    try {
      await putConversationPreferences(patch, source);
      return true;
    } catch (error) {
      if (isPreferencesForbidden(error)) return false;
      console.error('Failed to save conversation preferences:', error);
    }
  }
  return false;
};
